import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject, Injector } from '@angular/core';
import { hasRecordLocation } from '../../../../../../../../../shared/helpers/design-context';
import { confirmLinkRemoval, linkHeldStrokes } from '../../../../../../../../../shared/helpers/link-removal';
import { DrawingsAvailabilityService } from '../../../../../../../../../shared/services/drawings-availability.service';
import { DrawingsAnalytics } from '../../../../../../../../../modules/projects/pages/projects-details-page/components/tab-screens/drawings/drawings-analytics';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import * as fromRoot from '../../../../../../../../../state/app.state';
import { DailyReportService } from '../../../daily-report.service';
import { ProjectsService } from '../../../../../../../core/projects.service';
import { CommonService } from '../../../../../../../../../shared/services/common.service';
import { currentLocation, projectDetails, siteDetails } from '../../../../../../../core/projects.selectors';
import { dailyReportLocationConfig } from '../../../../../../../../../shared/core/shared.selectors';
import { LocationPickerLauncherService } from '../../../../../../../../../shared/services/location-picker-launcher.service';
import { StationRangeComponent } from '../control-forms-details/station-range/station-range.component';
// Reuse the pure, exported helpers from the (frozen) control-forms renderer —
// importing these exported free functions does NOT touch that component's logic.
import {
  buildSignatureRoleOptions,
  buildSignatureView,
  mergeSignatureEmission,
  QUESTION_DEFAULT_OPTIONS,
  SignatureView,
} from '../control-forms-details/control-forms-details/control-forms-details.component';
import { SignatureComponent } from '../control-forms-details/signature/signature.component';
import { QuestionsComponent } from '../control-forms-details/questions/questions.component';
import {
  Block,
  buildRowSummary,
  formatStoredTimeForDisplay,
  isTimeField,
  normalizeFormToBlocks,
  rowValuesFromLine,
  selectTableLines,
  timeCompanionValues,
} from './form-blocks.util';

/**
 * Generic Composable Forms — WEB renderer.
 *
 * Renders a `display_format='generic'` control form as an ORDERED stack of
 * presentation-agnostic blocks produced by `normalizeFormToBlocks(form)`:
 *   header      -> header fields (same field renderer as control-forms). A header
 *                  block is either the untagged leading block or a GROUP of header
 *                  fields tagged with a ui_config.sections entry, rendered under
 *                  that section's heading. Groups are never gated by the Section
 *                  selector (which lists QUESTION sections only).
 *   questions   -> a SUMMARY CARD ("<section> — n/m answered") that opens the OLD
 *                  checklist `QuestionsComponent` dialog (reused as-is) to answer
 *                  Y/N/NA — NOT inline pills.
 *   table       -> repeating report_form_line rows (add/delete), honoring
 *                  presentation (default accordion).
 *   signatures  -> the shared signature section + capture dialog.
 *   footer      -> trailing form-level fields (ui_config.footer.fields), rendered
 *                  with the same field renderer and stored like header fields.
 *
 * Storage (matches the frozen checklist + mobile commit 3ae01b2c):
 *  - Header AND footer block answers -> top-level `field_values` (keyed by field
 *    id), exactly like header_lines/testing forms store header fields.
 *  - Questions block answers (Y/N/NA) -> `lines` (one report_form_line per
 *    answered question; `field_values = [{ id: <questionFieldId>, value }]`),
 *    EXACTLY like the old checklist + mobile. NOT top-level field_values, NOT
 *    custom_field_value.
 *  - Table block rows -> `lines` (report_form_line rows keyed by column ids).
 *  - Signatures -> the shared buildSignaturesPayload pipeline.
 *
 * Question-lines and table-lines coexist in `lines` but are told apart by field
 * id: a question-line's field_values[0].id is a form-level question field id
 * (block.questions[].id); a table-line's ids are column ids (block.columns[].id).
 * The two id sets never overlap, so table rehydration/filtering ignores
 * question-lines and vice versa. See DailyReportService.formatGenericForms().
 *
 * The two existing renderers (control-forms-details, testing-from) are frozen;
 * this component reuses their exported pure helpers AND the QuestionsComponent
 * dialog (which is itself frozen).
 */
@Component({
  selector: 'app-generic-form',
  templateUrl: './generic-form.component.html',
  styleUrls: ['./generic-form.component.scss'],
  standalone: false,
})
export class GenericFormComponent implements OnInit, OnDestroy {
  /** W49: "Mark on drawing" in the location menu, for projects with published sheets */
  hasPublishedSheets = false;
  private sheetsCheckedFor: string | null = null;
  private readonly drawingsAvailability = inject(DrawingsAvailabilityService);
  private readonly drawingsTrack = inject(DrawingsAnalytics);
  private readonly injector = inject(Injector);
  @Input() data: any;
  // Read-only display mode (e.g. map drilldown panel). Mirrors the control-forms
  // renderer's `options.disabled` contract.
  // `preview: true` is set by Settings > Forms, which renders a BLANK instance
  // inside a phone frame. The form stays interactive so sections and rows can be
  // explored, but the dialogs stay shut — see dialogsSuppressed.
  @Input() options: { disabled?: boolean, preview?: boolean } = {};

  get readOnly(): boolean {
    return !!this.options?.disabled;
  }

  get isPreview(): boolean {
    return !!this.options?.preview;
  }

  // In preview the questions screen is drawn INSIDE the phone frame (the field
  // app pushes a screen; a web dialog would break out of it), so the block is
  // handed to the host instead of opening the dialog.
  @Output() previewQuestions = new EventEmitter<{ label: string, questions: any[], options: string[] }>();

  // Same for the signature sheet — the field app pushes its own screen.
  @Output() previewSignature = new EventEmitter<void>();

  /**
   * Dialogs open as full-page overlays, which in the Settings > Forms preview
   * escape the phone frame entirely and invite answering a form that is never
   * saved. Preview keeps the summary cards; tapping them does nothing.
   */
  private get dialogsSuppressed(): boolean {
    return this.readOnly || this.isPreview;
  }

  // Root form: header + question field controls live here directly; each table
  // row is a nested FormGroup keyed `line_<n>` (a flat key namespace per table
  // block, prefixed by section so blocks don't collide).
  form = new FormGroup<any>({
    formType: new FormControl({ value: '', disabled: false }, [Validators.required]),
    latitude: new FormControl({ value: null, disabled: false }),
    longitude: new FormControl({ value: null, disabled: false }),
    location: new FormControl({ value: null, disabled: false }),
    comment: new FormControl({ value: null, disabled: false }),
    pictures: new FormControl({ value: JSON.stringify([]), disabled: false }),
    videos: new FormControl({ value: JSON.stringify([]), disabled: false }),
    // Drives the QUESTION-section selector (the "Section" multi-select). Reuses the
    // control name control-forms-details uses so its dropdown markup is reused as-is.
    frequency: new FormControl({ value: null, disabled: false }),
    // Station-range controls — mirror control-forms-details exactly so the reused
    // StationRangeComponent dialog + DailyReportService.setStationRange write to the
    // same control names, and formatGenericForms can build the same payload shape.
    stationRangeFromStation: new FormControl({ value: null, disabled: false }),
    stationRangeFromOffsetType: new FormControl({ value: null, disabled: false }),
    stationRangeFromOffsetDistance: new FormControl({ value: null, disabled: false }),
    stationRangeToStation: new FormControl({ value: null, disabled: false }),
    stationRangeToOffsetType: new FormControl({ value: null, disabled: false }),
    stationRangeToOffsetDistance: new FormControl({ value: null, disabled: false }),
    alignment: new FormControl({ value: null, disabled: false }),
    map_feature_link: new FormControl(null),
  });

  // Action-bar driven UI state (mirrors control-forms-details). The action bar
  // (app-actions-section) communicates only through shared services, not @Outputs,
  // so the renderer must subscribe to commonService.uploadedFilesInfo /
  // dailyReportService.selectedLocation / dailyReportService.addComment.
  mediaFiles: any[] = [];
  displayCommentSection = false;

  // Station-range display/seed state (mirrors control-forms-details).
  displayStationRange = false;
  stationRange: any = {};
  stationRangeDisplayValue: any = null;
  alignment: any;
  dailyReportLocationConfig: any;

  blocks: Block[] = [];
  formTemplate: any;

  // Flat list of HEADER + FOOTER fields whose answers go into top-level field_values.
  // Drives formatGenericForms()'s field_values build. Question fields are NOT
  // here — their answers are stored as report_form_lines (see questionFields).
  fieldValueFields: any[] = [];
  // Union of every table column field, used to map line controls -> field ids.
  tableFields: any[] = [];
  // Union of every QUESTION field across question blocks, used to harvest the
  // per-block question groups into question-lines on save.
  questionFields: any[] = [];

  // Per-table-block UI state, keyed by a stable block key.
  tableState: Record<string, { keys: string[]; expanded: Record<string, boolean>; nextIndex: number }> = {};

  // rowKey -> owning table block's section_name (null for a sectionless block).
  // Threaded through to the save payload so each emitted table `line` carries
  // `section_name`, matching how mobile stamps + filters rows (cross-platform
  // round-trip: web-saved rows must reopen in the right table block on mobile).
  private rowSectionName: Record<string, string | null> = {};

  // Per-question-block UI/storage state, keyed by questionBlockKey(block).
  //  - groupName: the name of the FormGroup on the root form that the reused
  //    QuestionsComponent dialog reads/writes. Sectionless blocks use 'questions'
  //    (the general-question dialog form); section blocks use '<section>_questions'.
  //  - fields:    this block's question fields (passed to the dialog).
  //  - responseOptions: this block's Y/N/NA options (passed to the dialog).
  //  - answered:  cached count of answered questions (drives the card caption).
  questionBlockState: Record<
    string,
    { groupName: string; fields: any[]; responseOptions: string[]; answered: number }
  > = {};

  // ---- Section selection gating for QUESTION sections ----
  // Mirrors control-forms-details' `frequency` ("Section") selector: a multi-select
  // dropdown over the form's question sections controls which question-section
  // summary cards appear. Sectionless ("general") questions are NOT gated; table
  // sections are NOT gated either (they stay always-visible).
  //  - sectionSelectorPresent: there is >=1 NAMED question section to gate.
  //  - sectionOptions: { name, description } per named question section, fed to
  //    the dropdown (value = section name, matching control-forms frequencyOptions).
  //  - selectedSections: the set of section names the user has chosen to show.
  // The control name `frequency` matches control-forms so the same dropdown markup
  // is reused byte-for-byte.
  sectionSelectorPresent = false;
  sectionOptions: { name: string; description?: string; value: string; label: string }[] = [];
  // Label shown above the section-selector dropdown. Defaults to "Section"
  // (matching the old checklist), overridable per form via
  // ui_config.section_picker_label so a form can show e.g. "Permit Type".
  sectionPickerLabel = 'Section';
  private selectedSections = new Set<string>();

  // Signatures (shared model with control-forms renderer).
  signatures: any[] = [];
  signatureViews: SignatureView[] = [];
  signaturesBlockPresent = false;
  signatureRoleOptions: { label: string; value: string }[] = [];

  picklistOptions: Record<string, any[]> = {};
  project: any;

  // Guards against rebuilding controls/rows when the projectDetails store slice
  // re-emits (it can fire multiple times); the form is built once per instance.
  private built = false;

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private dailyReportService: DailyReportService,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private cdRef: ChangeDetectorRef,
    public dialog: MatDialog,
    private locationPickerLauncher: LocationPickerLauncherService,
  ) {}

  ngOnInit() {
    this.store
      .select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        this.project = project;
        this.formTemplate = this.resolveFormTemplate(project);
        this.checkPublishedSheets(project?.id);
        this.buildFromTemplate();
      });

    // Drives station-range visibility from the project's daily-report location
    // config (mirrors control-forms-details: the station-range section shows when
    // the location mode is 'station_range' for the control_forms widget).
    this.store
      .select(dailyReportLocationConfig)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(config => {
        this.dailyReportLocationConfig = config;
        this.displayStationRange = this.shouldDisplayStationRange(this.formTemplate);
      });

    if (this.data?.signatures?.length) {
      this.signatures = JSON.parse(JSON.stringify(this.data.signatures));
      this.refreshSignatureViews();
    }

    // Rehydrate action-bar driven state from the saved instance (mirrors
    // control-forms-details): show captured media, reveal the comment field when a
    // comment was saved, and seed the station range so it reopens populated.
    if (this.data) {
      this.mediaFiles = [...(this.data?.pictures || []), ...(this.data?.videos || [])];

      if (this.data?.comment) {
        this.displayCommentSection = true;
      }

      if (this.isValidObject(this.data?.station_range?.to) || this.isValidObject(this.data?.station_range?.from)) {
        this.displayStationRange = true;
        this.setStationRange(this.data?.station_range);
      }
    }

    if (this.readOnly) {
      this.form.disable({ emitEvent: false });
      return;
    }

    this.form.controls['formType'].disable({ emitEvent: false });

    // Photos/videos tapped in the action bar arrive on commonService.uploadedFilesInfo.
    // Generic has no per-line action-bar media selection (the simple legacy `else`
    // path) — route every captured file to the FORM-LEVEL pictures/videos JSON
    // controls, and mirror it into mediaFiles so the preview renders.
    this.commonService.uploadedFilesInfo.pipe(takeUntil(this.onDestroy)).subscribe(data => {
      const items = Array.isArray(data) ? data : [data];
      items.forEach((eachItem: any) => {
        const fileType = eachItem?.fileType;
        if (!fileType) {
          return;
        }
        if (fileType === 'picture') {
          const pictures = this.getPictures(this.form);
          pictures.push(eachItem);
          this.form.get('pictures')?.setValue(JSON.stringify(pictures));
        } else if (fileType === 'video') {
          const videos = this.getVideos(this.form);
          videos.push(eachItem);
          this.form.get('videos')?.setValue(JSON.stringify(videos));
        }
        this.mediaFiles.push(eachItem);
      });
      this.mediaFiles = [...this.mediaFiles];
    });

    // Location/station picked in the action bar arrives on selectedLocation: patch
    // latitude/longitude/location, and seed the station range when present.
    this.dailyReportService.selectedLocation.pipe(takeUntil(this.onDestroy)).subscribe(location => {
      if ('latitude' in (location || {}) || 'longitude' in (location || {})) {
        this.form.patchValue({
          latitude: location?.latitude,
          longitude: location?.longitude,
        });
      }

      if ('location' in (location || {}) || 'locationText' in (location || {})) {
        this.form.patchValue({
          location: location?.location ?? location?.locationText ?? null,
        });
      }

      // Only when the picker sent it: a Google / station-range pick leaves the link untouched.
      if ('map_feature_link' in (location || {})) {
        this.form.patchValue({ map_feature_link: location.map_feature_link });
        this.form.get('map_feature_link')?.markAsDirty();
      }

      if (location?.station_range) {
        this.displayStationRange = true;
        this.setStationRange(location.station_range);
      }
    });

    // The action bar's "Add comment" reveals the comment field (mirrors legacy).
    this.dailyReportService.addComment.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.displayCommentSection = true;
    });

    // The StationRangeComponent dialog emits the picked range here (mirrors legacy);
    // re-seed the controls + display value so the card updates.
    this.dailyReportService.setStationRange.pipe(takeUntil(this.onDestroy)).subscribe((data: any) => {
      this.displayStationRange = true;
      this.setStationRange({
        ...data?.stationRange,
        alignment: data?.alignment,
      });
      this.cdRef.detectChanges();
    });

    this.dailyReportService.setSignature.pipe(takeUntil(this.onDestroy)).subscribe(emission => {
      const idx = emission?.index;
      const isExisting = typeof idx === 'number' && idx >= 0 && idx < this.signatures.length;
      if (isExisting) {
        this.signatures[idx] = mergeSignatureEmission(this.signatures[idx], emission);
      } else {
        this.signatures.push(mergeSignatureEmission(null, emission));
      }
      this.refreshSignatureViews();
      this.cdRef.detectChanges();
    });

    this.dailyReportService.triggerSave.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.dailyReportService.formatGenericForms(
        this.form,
        this.fieldValueFields,
        this.tableFields,
        this.collectLineGroups(),
        this.collectQuestionGroups(),
        this.data,
        this.signatures,
      );
    });

    this.dailyReportService.triggerDelete.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.dailyReportService.delete.next({ type: 'forms', id: this.data?.id });
    });
  }

  /**
   * Resolve the enriched control_form template for this instance from the store
   * project (so header/table fields carry derived componentType + normalized
   * pick lists), falling back to the raw template carried on the instance.
   */
  private resolveFormTemplate(project: any): any {
    const controlFormId = this.data?.control_form?.id;
    const storeForms = project?.control_forms || [];
    return storeForms.find((f: any) => f?.id === controlFormId) || this.data?.control_form || null;
  }

  /** Build blocks + form controls from the resolved template (once per instance). */
  private buildFromTemplate() {
    if (!this.formTemplate || this.built) {
      return;
    }
    this.built = true;

    this.blocks = normalizeFormToBlocks(this.formTemplate);
    this.fieldValueFields = [];
    this.tableFields = [];
    this.questionFields = [];
    this.signaturesBlockPresent = false;

    this.form.patchValue(
      {
        formType: this.data?.control_form?.id ?? this.formTemplate?.id,
        comment: this.data?.comment ?? null,
        latitude: this.data?.latitude ?? null,
        longitude: this.data?.longitude ?? null,
        map_feature_link: this.data?.map_feature_link ?? null,
        pictures: this.data?.pictures ? JSON.stringify(this.data.pictures) : JSON.stringify([]),
        videos: this.data?.videos ? JSON.stringify(this.data.videos) : JSON.stringify([]),
      },
      { emitEvent: false },
    );

    const savedFieldValues: any[] = this.data?.field_values || [];

    // Pre-collect every question field id BEFORE initializing table blocks, so a
    // table block can positively exclude question-lines (a saved question-line's
    // first field id is a question field id) regardless of block order — even a
    // sectionless table that would otherwise claim a sectionless-looking line.
    this.questionFields = [];
    this.blocks.forEach(block => {
      if (block.kind === 'questions') {
        block.questions.forEach(field => {
          if (!this.questionFields.find(f => f.id === field.id)) {
            this.questionFields.push(field);
          }
        });
      }
    });

    this.blocks.forEach(block => {
      if (block.kind === 'header' || block.kind === 'footer') {
        // Footer fields are form-level fields: same control registration, same
        // top-level field_values storage + rehydration as header fields.
        block.fields.forEach(field => this.registerFieldValueControl(field, savedFieldValues));
      } else if (block.kind === 'questions') {
        this.initQuestionBlock(block);
      } else if (block.kind === 'table') {
        this.initTableBlock(block, savedFieldValues);
      } else if (block.kind === 'signatures') {
        this.signaturesBlockPresent = true;
        this.signatureRoleOptions = buildSignatureRoleOptions({ signature_roles: block.roles });
      }
    });

    this.buildPicklistOptions();
    // Wire allow_other companion inputs for header dropdown fields AFTER picklists
    // are built (the seed branch checks the field's picklist values).
    this.fieldValueFields.forEach(field => this.wireAllowOther(field));
    this.initSectionSelector();

    if (this.readOnly) {
      this.form.disable({ emitEvent: false });
    }
  }

  // ---- Section selection gating for QUESTION sections ----

  /**
   * Reproduce control-forms-details' "Section" selector gating for QUESTION
   * sections. NAMED question-section blocks (sectionName != null) are gated behind
   * a multi-select dropdown; only chosen sections' summary cards render. The
   * sectionless ("general") question block and ALL table blocks are never gated.
   *
   * Mirrors control-forms-details:
   *  - frequencyOptions          -> sectionOptions (one per named question section),
   *  - form.controls.frequency   -> the multi-select control,
   *  - displayFrequencySection() -> selectedSections (the chosen section names),
   *  - on load, pre-select sections that already have answers (so a rehydrated
   *    form shows its answered cards), matching how control-forms seeds frequency
   *    from segregateLinesBySection.
   */
  private initSectionSelector() {
    const questionSectionBlocks = this.blocks.filter(
      (b): b is Extract<Block, { kind: 'questions' }> => b.kind === 'questions' && b.sectionName != null,
    );

    this.sectionSelectorPresent = questionSectionBlocks.length > 0;
    const configuredLabel = this.formTemplate?.ui_config?.section_picker_label;
    this.sectionPickerLabel =
      typeof configuredLabel === 'string' && configuredLabel.trim() ? configuredLabel.trim() : 'Section';
    this.sectionOptions = questionSectionBlocks.map(b => ({
      name: b.sectionName as string,
      description: b.label || (b.sectionName as string),
      // control-forms' dropdown reads { value, label }; value === section name so
      // displayFrequencySection / the selectedSections match works on names.
      value: b.sectionName as string,
      label: b.label || (b.sectionName as string),
    }));

    if (!this.sectionSelectorPresent) {
      return;
    }

    // Recompute the visible-section set whenever the selector changes.
    this.form.controls['frequency'].valueChanges.pipe(takeUntil(this.onDestroy)).subscribe((data: any) => {
      this.applySelectedSections(data);
    });

    // Pre-select any section that already has answers so its card shows on reopen
    // (mirrors control-forms seeding frequency from saved answers).
    const preselected = questionSectionBlocks
      .filter(b => this.answeredCount(b) > 0)
      .map(b => b.sectionName as string);
    if (preselected.length) {
      this.form.controls['frequency'].setValue(preselected, { emitEvent: false });
      this.applySelectedSections(preselected);
    }
  }

  /** Translate the multi-select value (array of section names) into the visible set. */
  private applySelectedSections(data: any) {
    const names: string[] = Array.isArray(data) ? data : data != null ? [data] : [];
    this.selectedSections = new Set<string>(names.filter(n => typeof n === 'string'));
  }

  /**
   * Whether a QUESTION block's summary card should render. Sectionless ("general")
   * question blocks are always visible; named question sections appear only when
   * selected in the section selector. (Table blocks are gated elsewhere — they are
   * always visible and never call this.)
   */
  isQuestionSectionVisible(block: Extract<Block, { kind: 'questions' }>): boolean {
    if (block.sectionName == null) {
      return true;
    }
    return this.selectedSections.has(block.sectionName);
  }

  /**
   * Register a control for a header OR question field on the root form. Both are
   * persisted to top-level field_values keyed by field id (Option U), so they
   * share this single registration path. Seeds the saved value when present.
   */
  private registerFieldValueControl(field: any, savedFieldValues: any[]) {
    this.fieldValueFields.push(field);
    const matched = savedFieldValues.find(fv => fv?.id === field?.id);
    let value: any = null;
    if (matched) {
      const v = matched.value;
      // When the saved value is an object (pick-list payload), pull the stored
      // primitive. Fall back across the shapes pick-list values use elsewhere
      // (value -> id -> name -> label) so an object like `{ label: 'x' }` isn't
      // dropped to null. Primitives (incl. 0/false/'') pass through untouched.
      if (v && typeof v === 'object') {
        value = v.value ?? v.id ?? v.name ?? v.label ?? null;
      } else {
        value = v ?? null;
      }
    }
    // A time field shows 12-hour text and needs the app-time-select companions
    // (see registerRowControl); storage stays 24-hour HH:MM:SS.
    const isTime = isTimeField(field);
    const controlValue = isTime ? formatStoredTimeForDisplay(value) : value;

    if (!this.form.contains(field.name)) {
      this.form.addControl(
        field.name,
        new FormControl(
          { value: controlValue, disabled: !!field?.display_only || this.readOnly },
          field?.is_optional === false ? [Validators.required] : [],
        ),
        { emitEvent: false },
      );
    } else {
      this.form.controls[field.name].setValue(controlValue, { emitEvent: false });
    }

    if (isTime) {
      const companions = timeCompanionValues(value);
      const disabled = !!field?.display_only || this.readOnly;
      this.addOrSetControl(`${field.name}_timePeriod`, companions.timePeriod, disabled);
      this.addOrSetControl(`${field.name}_am_pm`, companions.amPm, disabled);
    }
  }

  /** Add a root-form control, or reseed it when it already exists. */
  private addOrSetControl(name: string, value: any, disabled: boolean) {
    if (!this.form.contains(name)) {
      this.form.addControl(name, new FormControl({ value, disabled }), { emitEvent: false });
    } else {
      this.form.controls[name].setValue(value, { emitEvent: false });
    }
  }

  /**
   * Reproduce control-forms-details' header "allow_other" companion-input wiring.
   * For a dropdown field with `allow_other === true`, the app-dropdown injects an
   * "Others" option (value '_others'); selecting it must reveal a `<field>_others`
   * free-text control. Storage already collapses '_others' + `<field>_others` into
   * the field's saved value (DailyReportService.formatGenericForms), so this is a
   * pure UI control: add the companion when '_others' is selected, remove it
   * otherwise. On load, if the saved value isn't one of the field's picklist values,
   * surface it through the "Others" path so it round-trips (matching control-forms'
   * `type_of_visual` seeding).
   */
  private wireAllowOther(field: any) {
    const isDropdown = (field?.componentType || field?.type) === 'dropdown';
    if (!isDropdown || !field?.allow_other || this.readOnly) {
      return;
    }
    const othersName = `${field.name}_others`;
    const savedValue = this.form.controls[field.name]?.value;

    // Seed: a saved value not present in the picklist is an "other" answer.
    const pickValues: any[] = (
      field?.ui_config?.pick_list_values?.length
        ? field.ui_config.pick_list_values
        : this.picklistOptions[field.name] || []
    ).map((o: any) => o?.value ?? o);
    const isOtherSeed =
      savedValue !== null && savedValue !== undefined && savedValue !== '' && !pickValues.includes(savedValue);

    if (isOtherSeed) {
      this.form.addControl(othersName, new FormControl({ value: savedValue, disabled: false }), { emitEvent: false });
      this.form.controls[field.name].setValue('_others', { emitEvent: false });
    }

    this.form.controls[field.name].valueChanges.pipe(takeUntil(this.onDestroy)).subscribe((d: any) => {
      if (d === '_others') {
        if (!this.form.contains(othersName)) {
          this.form.addControl(othersName, new FormControl({ value: null, disabled: false }));
        }
      } else {
        if (this.form.contains(othersName)) {
          this.form.removeControl(othersName);
        }
      }
    });
  }

  // ---- Table blocks ----

  tableBlockKey(block: Extract<Block, { kind: 'table' }>): string {
    return `table_${block.sectionName || 'general'}`;
  }

  questionBlockKey(block: Extract<Block, { kind: 'questions' }>): string {
    return `questions_${block.sectionName || 'general'}`;
  }

  /** Root-form control name holding a question block's group (`q_<blockKey>`). */
  questionGroupName(block: Extract<Block, { kind: 'questions' }>): string {
    return `q_${this.questionBlockKey(block)}`;
  }

  private initTableBlock(block: Extract<Block, { kind: 'table' }>, savedFieldValues: any[]) {
    const key = this.tableBlockKey(block);
    block.columns.forEach(col => {
      if (!this.tableFields.find(f => f.id === col.id)) {
        this.tableFields.push(col);
      }
    });

    this.tableState[key] = { keys: [], expanded: {}, nextIndex: 1 };
    // accordion = collapsed rows; inline rows render expanded.
    const expandOnAdd = block.presentation === 'inline';

    // Seed existing rows from saved report_form_lines. A line belongs to this
    // block when it carries at least one of the block's COLUMN ids — question
    // answers live in the same collection and are told apart only by field id,
    // so this is what keeps them from rendering as rows. The API does not return
    // `section_name` on a line, hence column ids (not sections) are the rule.
    const blockSection = block.sectionName || null;
    const savedLines = selectTableLines(this.data?.lines, block.columns, blockSection);

    savedLines.forEach((line: any) => {
      // Values are stored by field ID; row controls are keyed by field NAME.
      const values = rowValuesFromLine(line, block.columns);
      const rowKey = `${key}__line_${this.tableState[key].nextIndex++}`;
      const group = new FormGroup({});
      block.columns.forEach(col => {
        this.registerRowControl(group, col, values[col.name]);
      });
      group.addControl('pictures', new FormControl(JSON.stringify(line?.pictures || [])));
      group.addControl('videos', new FormControl(JSON.stringify(line?.videos || [])));
      this.form.addControl(rowKey, group, { emitEvent: false });
      this.tableState[key].keys.push(rowKey);
      this.rowSectionName[rowKey] = blockSection;
      this.tableState[key].expanded[rowKey] = expandOnAdd;
    });
  }

  /**
   * Add one column's control to a row group, seeded with `value`.
   *
   * A `time` column needs THREE controls: the value control (holding the 12-hour
   * display string the readonly input shows) plus the `<name>_timePeriod` and
   * `<name>_am_pm` companions the shared `app-time-select` overlay binds to.
   * Without the companions its picker renders empty and closing it nulls the
   * value. Storage stays 24-hour `HH:MM:SS` — see toStoredTime() on save.
   */
  private registerRowControl(group: FormGroup, col: any, value: any) {
    const disabled = !!col?.display_only || this.readOnly;
    const validators = col?.is_optional === false ? [Validators.required] : [];

    if (!isTimeField(col)) {
      group.addControl(col.name, new FormControl({ value: value ?? null, disabled }, validators));
      return;
    }

    const companions = timeCompanionValues(value);
    group.addControl(
      col.name,
      new FormControl({ value: formatStoredTimeForDisplay(value), disabled }, validators),
    );
    group.addControl(`${col.name}_timePeriod`, new FormControl({ value: companions.timePeriod, disabled }));
    group.addControl(`${col.name}_am_pm`, new FormControl({ value: companions.amPm, disabled }));
  }

  addTableRow(block: Extract<Block, { kind: 'table' }>) {
    if (this.readOnly) {
      return;
    }
    const key = this.tableBlockKey(block);
    const state = this.tableState[key];
    const rowKey = `${key}__line_${state.nextIndex++}`;
    const group = new FormGroup({});
    block.columns.forEach(col => {
      this.registerRowControl(group, col, col?.default_value ?? null);
    });
    group.addControl('pictures', new FormControl(JSON.stringify([])));
    group.addControl('videos', new FormControl(JSON.stringify([])));
    this.form.addControl(rowKey, group);
    state.keys.push(rowKey);
    this.rowSectionName[rowKey] = block.sectionName || null;
    // Collapse siblings, expand the new row (accordion UX).
    Object.keys(state.expanded).forEach(k => (state.expanded[k] = false));
    state.expanded[rowKey] = true;
    this.cdRef.detectChanges();
  }

  deleteTableRow(block: Extract<Block, { kind: 'table' }>, rowKey: string) {
    if (this.readOnly) {
      return;
    }
    const key = this.tableBlockKey(block);
    const state = this.tableState[key];
    this.form.removeControl(rowKey);
    state.keys = state.keys.filter(k => k !== rowKey);
    delete state.expanded[rowKey];
    delete this.rowSectionName[rowKey];
    this.cdRef.detectChanges();
  }

  toggleTableRow(block: Extract<Block, { kind: 'table' }>, rowKey: string) {
    const key = this.tableBlockKey(block);
    const state = this.tableState[key];
    state.expanded[rowKey] = !state.expanded[rowKey];
  }

  rowGroup(rowKey: string): FormGroup {
    return this.form.get(rowKey) as FormGroup;
  }

  /**
   * Accordion heading for a collapsed row: `Line 2 (2:30 PM, 5.5, 55)`.
   * Built from the ROW's own values — rendering a column label instead made
   * every row read the same ("Time"). Matches the mobile row summary.
   */
  rowHeading(block: Extract<Block, { kind: 'table' }>, rowKey: string): string {
    const state = this.tableState[this.tableBlockKey(block)];
    const position = (state?.keys?.indexOf(rowKey) ?? -1) + 1;
    return buildRowSummary(this.rowGroup(rowKey)?.getRawValue() || {}, block.columns, position || 1);
  }

  /**
   * Ordered list of every table row across all blocks (save path), each paired
   * with its owning block's section_name so the emitted `line` can be stamped
   * (cross-platform round-trip with mobile).
   */
  private collectLineGroups(): { group: FormGroup; sectionName: string | null }[] {
    const groups: { group: FormGroup; sectionName: string | null }[] = [];
    Object.values(this.tableState).forEach(state => {
      state.keys.forEach(rowKey => {
        const g = this.form.get(rowKey) as FormGroup;
        if (g) {
          groups.push({ group: g, sectionName: this.rowSectionName[rowKey] ?? null });
        }
      });
    });
    return groups;
  }

  // ---- Questions (summary card + reused QuestionsComponent dialog) ----

  /**
   * Build the per-block question FormGroup the reused QuestionsComponent dialog
   * reads/writes, and seed it from existing question-lines. Mirrors the old
   * checklist's createGeneralQuestionsFrom:
   *   group is keyed `<lineId|new_ts>_<fieldId>` -> { field_values, comment,
   *   pictures, videos }.
   * Each block stores its group under a UNIQUE name (`q_<blockKey>`); the dialog's
   * app-general-question-form only ever reads form.get('questions'), so
   * openQuestionDetails transiently aliases the active block there. This keeps
   * forms with multiple question blocks (e.g. a sectionless block + section
   * blocks) from clobbering one another's answers.
   */
  private initQuestionBlock(block: Extract<Block, { kind: 'questions' }>) {
    const key = this.questionBlockKey(block);
    const groupName = this.questionGroupName(block);

    // Existing question-lines for THIS block: a line whose field_values[0].id is
    // one of this block's question field ids (id sets across blocks/tables don't
    // overlap, so this strictly selects this block's answers).
    const questionIds = new Set(block.questions.map(q => q.id));
    const savedLines: any[] = (this.data?.lines || []).filter((line: any) =>
      questionIds.has(line?.field_values?.[0]?.id),
    );

    const group = new FormGroup<any>({});
    block.questions.forEach(field => {
      const matchingLine = savedLines.find((line: any) => line?.field_values?.[0]?.id === field.id);
      const controlKey = matchingLine
        ? `${matchingLine.id}_${field.id}`
        : `new_${new Date().getTime()}_${field.id}`;
      group.addControl(
        controlKey,
        new FormGroup({
          field_values: new FormControl(matchingLine?.field_values?.[0]?.value ?? null),
          comment: new FormControl(matchingLine?.comment ?? null),
          pictures: new FormControl(JSON.stringify(matchingLine?.pictures || [])),
          videos: new FormControl(JSON.stringify(matchingLine?.videos || [])),
        }),
      );
    });

    if (this.form.contains(groupName)) {
      this.form.removeControl(groupName, { emitEvent: false });
    }
    this.form.addControl(groupName, group, { emitEvent: false });

    this.questionBlockState[key] = {
      groupName,
      fields: block.questions,
      responseOptions: block.responseOptions,
      answered: this.countAnswered(group),
    };
  }

  /** Count answered questions in a block's group (non-empty field_values). */
  private countAnswered(group: FormGroup): number {
    let count = 0;
    Object.values(group.controls).forEach((ctrl: any) => {
      const value = ctrl?.controls?.['field_values']?.value;
      if (value !== null && value !== undefined && value !== '') {
        count++;
      }
    });
    return count;
  }

  /** "<n>" answered for the block card. */
  answeredCount(block: Extract<Block, { kind: 'questions' }>): number {
    return this.questionBlockState[this.questionBlockKey(block)]?.answered ?? 0;
  }

  /** Total questions for the block card. */
  questionTotal(block: Extract<Block, { kind: 'questions' }>): number {
    return block.questions?.length ?? 0;
  }

  /**
   * Open the OLD checklist QuestionsComponent dialog for this question block.
   * The dialog's app-general-question-form reads/writes form.get('questions'),
   * so point that control at the active block's group before opening, then
   * recompute the answered count once the dialog closes.
   */
  openQuestionDetails(block: Extract<Block, { kind: 'questions' }>) {
    const key = this.questionBlockKey(block);
    const state = this.questionBlockState[key];

    if (this.isPreview) {
      this.previewQuestions.emit({
        label: block.label || 'Questions',
        questions: state?.fields?.length ? state.fields : (block.questions || []),
        options: state?.responseOptions?.length ? state.responseOptions : QUESTION_DEFAULT_OPTIONS,
      });
      return;
    }

    if (this.dialogsSuppressed) {
      return;
    }
    if (!state) {
      return;
    }

    // The reused general-question dialog form reads/writes form.get('questions');
    // transiently alias the active block's group there. Only one dialog is open
    // at a time, so a single shared 'questions' alias is safe.
    const blockGroup = this.form.get(state.groupName) as FormGroup;
    this.form.setControl('questions', blockGroup, { emitEvent: false });

    this.dialog
      .open(QuestionsComponent, {
        disableClose: true,
        width: '1200px',
        height: '620px',
        panelClass: 'custom-dialog-container',
        data: {
          fields: JSON.parse(JSON.stringify(state.fields)),
          form: this.form,
          type: 'generalQuestions',
          responseOptions: state.responseOptions || [],
        },
      })
      .afterClosed()
      .subscribe(() => {
        // The dialog mutates the SAME group object in place (alias), so the
        // block's group already holds the new answers — just recount.
        state.answered = this.countAnswered(blockGroup);
        this.cdRef.detectChanges();
      });
  }

  /**
   * Ordered list of every question group across all question blocks (save path).
   * formatGenericForms serializes these into question-lines (bare question field
   * id), matching mobile.
   */
  private collectQuestionGroups(): FormGroup[] {
    const groups: FormGroup[] = [];
    Object.values(this.questionBlockState).forEach(state => {
      const g = this.form.get(state.groupName) as FormGroup;
      if (g) {
        groups.push(g);
      }
    });
    return groups;
  }

  // ---- Signatures (shared with control-forms renderer) ----

  openSignatureDetails(index: number | null = null) {
    if (this.isPreview) {
      this.previewSignature.emit();
      return;
    }
    if (this.dialogsSuppressed) {
      return;
    }
    this.dialog.open(SignatureComponent, {
      disableClose: true,
      width: '580px',
      maxHeight: '90vh',
      data: {
        index,
        signer: index != null ? this.signatures?.[index] : null,
        form: this.form,
        options: this.signatureRoleOptions,
        // Capture mode: when true the dialog uses the draw pad; otherwise it
        // captures a typed signature name. Defaults to false (typed).
        signaturePadEnabled: !!this.formTemplate?.ui_config?.signature_pad?.enabled,
        // Per-form gate (draw-pad mode only): when true the printed name is
        // mandatory. Defaults to false when the template doesn't configure it.
        requirePrintedName: !!this.formTemplate?.ui_config?.signature_pad?.require_printed_name,
      },
    });
  }

  removeSignature(index: number) {
    if (this.readOnly) {
      return;
    }
    if (index < 0 || index >= (this.signatures?.length ?? 0)) {
      return;
    }
    this.signatures.splice(index, 1);
    this.refreshSignatureViews();
    this.cdRef.detectChanges();
  }

  private refreshSignatureViews() {
    this.signatureViews = (this.signatures || [])
      .map((s: any, i: number) => buildSignatureView(s, undefined, i))
      .filter((view: SignatureView) => view.text || view.imageUrl);
  }

  // ---- Pick lists for dropdown fields ----

  private buildPicklistOptions() {
    if (!this.project) {
      return;
    }
    const options: Record<string, any[]> = { ...this.picklistOptions };
    [...this.fieldValueFields, ...this.tableFields].forEach(field => {
      const [key, labelToUse] = field?.pick_list?.split?.('.') ?? [];
      if (!key) {
        return;
      }
      if (key === 'activity_codes') {
        options[field.name] = this.project?.activity_codes_forms ?? [];
      } else {
        const sourceArray = this.project?.[key] ?? [];
        options[field.name] = sourceArray.map((item: any) => ({
          ...item,
          label: item?.[labelToUse] ?? '',
          value: item?.[labelToUse] ?? '',
        }));
      }
    });
    this.picklistOptions = options;
  }

  /**
   * Whether a field's control spans the full width of the 2-column table-row grid.
   * Only consulted when the field carries `ui_config.instructions`: control +
   * helper text are then wrapped together, and the wrapper has to inherit the
   * col-span the control itself declares (textarea and boolean use col-span-2).
   */
  isFullWidthField(field: any): boolean {
    const type = field?.componentType || field?.type;
    return type === 'textarea' || type === 'boolean';
  }

  /**
   * Boolean slide-toggle handler. Table-row boolean columns live on the row's
   * nested FormGroup (`group`), not the root form — set on `group` when given,
   * else fall back to the root form for header/section boolean fields.
   */
  onToggle($event: any, controlName: string, group?: FormGroup) {
    (group?.controls?.[controlName] || this.form.controls[controlName])?.setValue($event?.checked);
  }

  // ---- Media (action-bar photos/videos) ----

  getPictures(form: FormGroup): any[] {
    try {
      const pictures = form.controls['pictures']?.value;
      return JSON.parse(pictures) || [];
    } catch (e) {
      return [];
    }
  }

  getVideos(form: FormGroup): any[] {
    try {
      const videos = form.controls['videos']?.value;
      return JSON.parse(videos) || [];
    } catch (e) {
      return [];
    }
  }

  /** Remove a captured picture/video from the form-level JSON control + preview. */
  deletePicture(file: any) {
    if (this.readOnly) {
      return;
    }
    if (file?.fileType === 'picture') {
      const pictures = this.getPictures(this.form).filter((p: any) => p.id !== file.id);
      this.form.get('pictures')?.setValue(JSON.stringify(pictures));
    } else if (file?.fileType === 'video') {
      const videos = this.getVideos(this.form).filter((v: any) => v.id !== file.id);
      this.form.get('videos')?.setValue(JSON.stringify(videos));
    }
    this.mediaFiles = this.mediaFiles.filter((m: any) => m.id !== file.id);
  }

  // ---- Location / station range (action-bar + StationRange dialog) ----

  /** Whether the lat/lng location card should render (mirrors control-forms). */
  showLocationSection(): boolean {
    const hasGps = this.form?.controls?.['latitude']?.value !== null &&
      this.form?.controls?.['longitude']?.value !== null &&
      this.form?.controls?.['latitude']?.value !== '' &&
      this.form?.controls?.['longitude']?.value !== '';
    // W50: a sheet-only drawing link (no pin) is the location too; the shared card shows its strip
    return hasRecordLocation({ hasGps, link: this.form?.controls?.['map_feature_link']?.value?.sheet ? this.form.controls['map_feature_link'].value : null });
  }

  showLocationOrStationSection(): boolean {
    return this.showLocationSection() || this.checkStationFormData();
  }

  /** True when any station-range control holds a value (mirrors control-forms). */
  checkStationFormData(): boolean {
    return !!(
      this.form.controls?.['stationRangeFromStation']?.value ||
      this.form.controls?.['stationRangeFromOffsetType']?.value ||
      this.form.controls?.['stationRangeFromOffsetDistance']?.value ||
      this.form.controls?.['stationRangeToStation']?.value ||
      this.form.controls?.['stationRangeToOffsetType']?.value ||
      this.form.controls?.['stationRangeToOffsetDistance']?.value ||
      this.form.controls?.['alignment']?.value
    );
  }

  isValidObject(obj: any): boolean {
    return obj && Object.values(obj).some(value => value !== null && value !== undefined && value !== '');
  }

  isStationRangeLocationEnabledForForms(): boolean {
    const mode = [
      this.project?.daily_report_location_mode,
      this.project?.owner_organization?.daily_report_location_mode,
      this.dailyReportLocationConfig?.mode,
    ].find(value => ['pin', 'station_range'].includes(value));

    if (mode !== 'station_range') {
      return false;
    }

    const widgets = this.dailyReportLocationConfig?.widgets || [];
    return !widgets.length || widgets.includes('control_forms');
  }

  /**
   * Mirror control-forms-details: show the station-range section when the form's
   * ui_config opts in (display_station_range / allow_station_range) OR the project's
   * daily-report location mode enables station ranges for the control_forms widget.
   */
  private shouldDisplayStationRange(formTemplate: any): boolean {
    return (
      !!formTemplate?.ui_config?.display_station_range ||
      !!formTemplate?.ui_config?.allow_station_range ||
      this.isStationRangeLocationEnabledForForms()
    );
  }

  /** Seed station-range state + controls from a station_range object (mirrors legacy). */
  private setStationRange(stationRange: any = {}) {
    this.stationRange = {
      from: {
        station: stationRange?.from?.station,
        offsetType: stationRange?.from?.offsetType ?? stationRange?.from?.offset_type,
        offsetDistance: stationRange?.from?.offsetDistance ?? stationRange?.from?.offset_distance,
      },
      to: {
        station: stationRange?.to?.station,
        offsetType: stationRange?.to?.offsetType ?? stationRange?.to?.offset_type,
        offsetDistance: stationRange?.to?.offsetDistance ?? stationRange?.to?.offset_distance,
      },
      alignment: stationRange?.alignment,
    };
    this.alignment = stationRange?.alignment;
    this.form?.patchValue({
      stationRangeFromStation: this.stationRange?.from?.station,
      stationRangeFromOffsetType: this.stationRange?.from?.offsetType,
      stationRangeFromOffsetDistance: this.stationRange?.from?.offsetDistance,
      stationRangeToStation: this.stationRange?.to?.station,
      stationRangeToOffsetType: this.stationRange?.to?.offsetType,
      stationRangeToOffsetDistance: this.stationRange?.to?.offsetDistance,
      alignment: this.alignment,
    });
    this.stationRangeDisplayValue = this.buildStationRangeDisplayValue();
  }

  /** Build the display model the location-station card reads (mirrors legacy). */
  private buildStationRangeDisplayValue() {
    if (!this.checkStationFormData()) {
      return null;
    }
    return {
      ...(this.stationRange || {}),
      from: {
        station: this.stationRange?.from?.station ?? this.form?.controls?.['stationRangeFromStation']?.value,
        offsetType: this.stationRange?.from?.offsetType ?? this.form?.controls?.['stationRangeFromOffsetType']?.value,
        offsetDistance:
          this.stationRange?.from?.offsetDistance ?? this.form?.controls?.['stationRangeFromOffsetDistance']?.value,
      },
      to: {
        station: this.stationRange?.to?.station ?? this.form?.controls?.['stationRangeToStation']?.value,
        offsetType: this.stationRange?.to?.offsetType ?? this.form?.controls?.['stationRangeToOffsetType']?.value,
        offsetDistance:
          this.stationRange?.to?.offsetDistance ?? this.form?.controls?.['stationRangeToOffsetDistance']?.value,
      },
      alignment: this.alignment ?? this.stationRange?.alignment ?? this.form?.controls?.['alignment']?.value,
    };
  }

  openLocationOrStationDetails() {
    if (this.checkStationFormData() || this.displayStationRange) {
      this.openStationRangeDetails();
    }
  }

  openStationRangeDetails() {
    if (this.dialogsSuppressed) {
      return;
    }
    this.dialog.open(StationRangeComponent, {
      disableClose: true,
      width: '1200px',
      height: '620px',
      maxWidth: 'calc(100vw - 48px)',
      maxHeight: 'calc(100vh - 48px)',
      panelClass: 'station-range-dialog-panel',
      data: {
        form: this.form,
      },
    });
  }

  /** W49: whether the project has published sheets, for "Mark on drawing" in the location menu (once per project) */
  private checkPublishedSheets(projectId: string | null | undefined): void {
    if (!projectId || projectId === this.sheetsCheckedFor) {
      return;
    }
    this.sheetsCheckedFor = projectId;
    this.hasPublishedSheets = false;
    this.drawingsAvailability.available(projectId).then(found => {
      if (this.sheetsCheckedFor === projectId) {
        this.hasPublishedSheets = found;
        this.cdRef.markForCheck();
      }
    }).catch(() => undefined);
  }

  /** W49: "Mark on drawing": the location picker, on its Drawing tab */
  markOnDrawing() {
    this.drawingsTrack.track('mark_on_drawing_clicked', { record_type: 'form' });
    this.openLocationPicker('drawing');
  }

  openLocationPicker(initialTab?: 'drawing') {
    if (this.dialogsSuppressed) {
      return;
    }
    const formLat = this.form?.controls?.['latitude']?.value;
    const formLng = this.form?.controls?.['longitude']?.value;
    const locationText = this.form?.controls?.['location']?.value || '';

    if (formLat && formLng) {
      this.openPinPicker(formLat, formLng, locationText, initialTab);
      return;
    }

    this.store
      .select(currentLocation)
      .subscribe(location => {
        let loc = location;
        if (!loc?.latitude || !loc?.longitude) {
          this.store
            .select(siteDetails)
            .subscribe(site => {
              if (site?.longitude && site?.latitude) {
                loc = { latitude: site?.latitude, longitude: site?.longitude };
              } else {
                this.store
                  .select(projectDetails)
                  .subscribe(project => {
                    if (project?.longitude && project?.latitude) {
                      loc = { latitude: project?.latitude, longitude: project?.longitude };
                    }
                  })
                  .unsubscribe();
              }
            })
            .unsubscribe();
        }
        this.openPinPicker(loc?.latitude, loc?.longitude, locationText, initialTab);
      })
      .unsubscribe();
  }

  private openPinPicker(latitude: number | string | null, longitude: number | string | null, locationText: string, initialTab?: 'drawing') {
    this.locationPickerLauncher.open({
      latitude,
      longitude,
      enableLocationSection: true,
      locationText,
      // Null when the form has no link control: the pick then leaves the record's link untouched unless the user links.
      existingLink: this.form?.get('map_feature_link')?.value ?? null,
      recordLabel: 'form',
      ...(initialTab ? { initialTab } : {}),
    }).catch(error => console.error('Location picker failed to open', error));
  }

  /** W77 (review I1): the header "…" Clear drops the link too, so markup on it asks first (link-removal.ts) */
  deleteLocation() {
    const link = this.form?.get('map_feature_link')?.value ?? null;
    confirmLinkRemoval(this.dialog, link, linkHeldStrokes(this.injector, link)).subscribe(go => {
      if (go) this.clearLocationState();
    });
  }

  /** Clear location + station-range state (mirrors control-forms deleteLocation). */
  private clearLocationState() {
    this.form?.get('latitude')?.setValue(null);
    this.form?.get('longitude')?.setValue(null);
    this.form?.get('location')?.setValue(null);
    // Deleting the location removes the link with it: an explicit removal (R54)
    if (this.form?.get('map_feature_link')?.value) this.form.get('map_feature_link').markAsDirty();
    this.form?.get('map_feature_link')?.setValue(null);
    this.form?.patchValue({
      stationRangeFromStation: null,
      stationRangeFromOffsetType: null,
      stationRangeFromOffsetDistance: null,
      stationRangeToStation: null,
      stationRangeToOffsetType: null,
      stationRangeToOffsetDistance: null,
      alignment: null,
    });
    this.stationRange = {};
    this.stationRangeDisplayValue = null;
    this.alignment = null;
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
